import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, TextField, InputAdornment, IconButton } from "@mui/material";
import BookOrderDialogShell from "../MultiAccountOrderModal/BookOrderDialogShell";
import ModalBidAskField, { DEFAULT_MODAL_BID_ASK_ORDER_TYPE } from "../MultiAccountOrderModal/ModalBidAskField";
import { QUANTITY_SPINNER_DOWN, QUANTITY_SPINNER_UP } from "../QuantitySpinnerIcons/QuantitySpinnerIcons";
import ToggleSwitch from "../ToggleSwitch";
import modalStyles from "../MultiAccountOrderModal/MultiAccountOrderModal.module.css";
import InstrumentTradingSymbolField from "../InstrumentTradingSymbolField";
import {
  buildRollOverConfirmPayload,
  buildRollOverGroupedData,
  collectRollOverModalIds,
  DEFAULT_SPREAD,
} from "./rollOverModalUtils";

function NumericSpinnerField({ value, onChange, onIncrement, onDecrement }) {
  return (
    <TextField
      size="small"
      type="number"
      value={value}
      onChange={onChange}
      className={modalStyles["modal-input"]}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <div className={modalStyles["spinner-controls"]}>
              <IconButton
                type="button"
                size="small"
                onClick={onIncrement}
                className={modalStyles["spinner-button"]}
                aria-label="Increase value"
              >
                {QUANTITY_SPINNER_UP}
              </IconButton>
              <IconButton
                type="button"
                size="small"
                onClick={onDecrement}
                className={modalStyles["spinner-button"]}
                aria-label="Decrease value"
              >
                {QUANTITY_SPINNER_DOWN}
              </IconButton>
            </div>
          </InputAdornment>
        ),
      }}
    />
  );
}

function RollOverModal({
  open,
  onClose,
  onConfirm,
  selectedRows = [],
  submitting = false,
  defaultSpread = DEFAULT_SPREAD,
  groupOptions,
}) {
  const [rowData, setRowData] = useState({});
  const [symbolOverrides, setSymbolOverrides] = useState({});
  const [globalQuantity, setGlobalQuantity] = useState("");
  const [globalMaxQuantity, setGlobalMaxQuantity] = useState("");
  const [globalSpread, setGlobalSpread] = useState("");
  const [globalSpreadOrderType, setGlobalSpreadOrderType] = useState(DEFAULT_MODAL_BID_ASK_ORDER_TYPE);
  const [globalMarginCheck, setGlobalMarginCheck] = useState(true);

  const groupedData = useMemo(
    () =>
      buildRollOverGroupedData(selectedRows, {
        defaultSpread,
        ...groupOptions,
      }),
    [selectedRows, defaultSpread, groupOptions]
  );

  const groupedDataKeys = useMemo(() => Object.keys(groupedData), [groupedData]);
  const hasGroupedData = groupedDataKeys.length > 0;

  const resetState = useCallback(() => {
    setRowData({});
    setSymbolOverrides({});
    setGlobalQuantity("");
    setGlobalMaxQuantity("");
    setGlobalSpread("");
    setGlobalSpreadOrderType(DEFAULT_MODAL_BID_ASK_ORDER_TYPE);
    setGlobalMarginCheck(true);
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  const handleClose = useCallback(() => {
    if (submitting) return;
    onClose?.();
  }, [onClose, submitting]);

  const handleRowDataChange = useCallback((modalId, field, value) => {
    setRowData((prev) => {
      const currentValue = prev[modalId]?.[field];
      if (currentValue === value) return prev;
      return {
        ...prev,
        [modalId]: {
          ...prev[modalId],
          [field]: value,
        },
      };
    });
  }, []);

  const handleTradingSymbolChange = useCallback((originalSymbol, newSymbol) => {
    setSymbolOverrides((prev) => ({
      ...prev,
      [originalSymbol]: newSymbol,
    }));
  }, []);

  const getRollOverTradingSymbol = useCallback(
    (originalSymbol) => symbolOverrides[originalSymbol] || originalSymbol,
    [symbolOverrides]
  );

  const getRowValue = useCallback(
    (row, field) => {
      const stateValue = rowData[row.modalId]?.[field];

      if (stateValue !== undefined) {
        return stateValue;
      }

      if (field === "quantity" && globalQuantity !== "") return globalQuantity;
      if (field === "maxQuantity" && globalMaxQuantity !== "") return globalMaxQuantity;
      if (field === "spread" && globalSpread !== "") return globalSpread;
      if (field === "spreadOrderType") return globalSpreadOrderType;
      if (field === "marginCheck") return row.marginCheck;

      return row[field] ?? "";
    },
    [rowData, globalQuantity, globalMaxQuantity, globalSpread, globalSpreadOrderType]
  );

  const applyGlobalFieldChange = useCallback(
    (field, value, setter) => {
      setter(value);
      setRowData((prev) => {
        const modalIds = collectRollOverModalIds(groupedData);
        const updated = { ...prev };
        let hasChanges = false;

        modalIds.forEach((modalId) => {
          if (updated[modalId]?.[field] !== value) {
            updated[modalId] = {
              ...updated[modalId],
              [field]: value,
            };
            hasChanges = true;
          }
        });

        return hasChanges ? updated : prev;
      });
    },
    [groupedData]
  );

  const parseNumeric = (raw) => {
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const createSpinnerHandlers = useCallback(
    (row, field) => ({
      onIncrement: () => {
        const current = parseNumeric(getRowValue(row, field));
        handleRowDataChange(row.modalId, field, current + 1);
      },
      onDecrement: () => {
        const current = parseNumeric(getRowValue(row, field));
        handleRowDataChange(row.modalId, field, Math.max(0, current - 1));
      },
    }),
    [getRowValue, handleRowDataChange]
  );

  const handleConfirm = useCallback(async () => {
    const payload = buildRollOverConfirmPayload(groupedData, rowData, symbolOverrides);
    await onConfirm?.(payload);
  }, [groupedData, onConfirm, rowData, symbolOverrides]);

  const header = useMemo(
    () => (
      <div className={modalStyles["modal-header-stacked"]}>
        <div className={modalStyles["modal-header"]}>
          <div className={modalStyles["modal-header-cell"]}>
            <span className={modalStyles["modal-header-label"]}>Trading Symbol</span>
          </div>
          <div className={modalStyles["modal-header-cell"]}>
            <span className={modalStyles["modal-header-label"]}>Reprice Trading Symbol</span>
          </div>
          <div className={modalStyles["modal-header-cell"]}>
            <span className={modalStyles["modal-header-label"]}>Accounts</span>
          </div>
          <div className={modalStyles["modal-header-cell"]}>
            <span className={`${modalStyles["modal-header-label"]} ${modalStyles["modal-header-label-qty"]}`}>
              Quantity
            </span>
          </div>
          <div className={modalStyles["modal-header-cell"]}>
            <span className={modalStyles["modal-header-label"]}>Max Quantity</span>
          </div>
          <div className={modalStyles["modal-header-cell"]}>
            <span className={modalStyles["modal-header-label"]}>Spread</span>
          </div>
          <div className={modalStyles["modal-header-cell"]}>
            <span className={modalStyles["modal-header-label"]}>Margin Check</span>
          </div>
        </div>
        <div className={`${modalStyles["modal-header"]} ${modalStyles["modal-header-inputs-row"]}`}>
          <div
            className={`${modalStyles["modal-header-cell"]} ${modalStyles["modal-header-inputs-spacer"]}`}
            aria-hidden="true"
          />
          <div
            className={`${modalStyles["modal-header-cell"]} ${modalStyles["modal-header-inputs-spacer"]}`}
            aria-hidden="true"
          />
          <div
            className={`${modalStyles["modal-header-cell"]} ${modalStyles["modal-header-inputs-spacer"]}`}
            aria-hidden="true"
          />
          <div className={modalStyles["modal-header-cell"]}>
            <div className={modalStyles["modal-header-input-wrap"]}>
              <NumericSpinnerField
                value={globalQuantity}
                onChange={(e) => applyGlobalFieldChange("quantity", e.target.value, setGlobalQuantity)}
                onIncrement={() => {
                  const next = parseNumeric(globalQuantity) + 1;
                  applyGlobalFieldChange("quantity", String(next), setGlobalQuantity);
                }}
                onDecrement={() => {
                  const next = Math.max(0, parseNumeric(globalQuantity) - 1);
                  applyGlobalFieldChange("quantity", String(next), setGlobalQuantity);
                }}
              />
            </div>
          </div>
          <div className={modalStyles["modal-header-cell"]}>
            <div className={modalStyles["modal-header-input-wrap"]}>
              <NumericSpinnerField
                value={globalMaxQuantity}
                onChange={(e) => applyGlobalFieldChange("maxQuantity", e.target.value, setGlobalMaxQuantity)}
                onIncrement={() => {
                  const next = parseNumeric(globalMaxQuantity) + 1;
                  applyGlobalFieldChange("maxQuantity", String(next), setGlobalMaxQuantity);
                }}
                onDecrement={() => {
                  const next = Math.max(0, parseNumeric(globalMaxQuantity) - 1);
                  applyGlobalFieldChange("maxQuantity", String(next), setGlobalMaxQuantity);
                }}
              />
            </div>
          </div>
          <div className={modalStyles["modal-header-cell"]}>
            <ModalBidAskField
              orderType={globalSpreadOrderType}
              onOrderTypeChange={(e) =>
                applyGlobalFieldChange("spreadOrderType", e.target.value, setGlobalSpreadOrderType)
              }
              value={globalSpread}
              onChange={(e) =>
                applyGlobalFieldChange("spread", e.target.value, (value) => setGlobalSpread(value))
              }
              disabled={submitting}
            />
          </div>
          <div className={modalStyles["modal-header-cell"]}>
            <ToggleSwitch
              checked={globalMarginCheck}
              onChange={(checked) => applyGlobalFieldChange("marginCheck", checked, setGlobalMarginCheck)}
              aria-label="Apply margin check to all rows"
            />
          </div>
        </div>
      </div>
    ),
    [
      applyGlobalFieldChange,
      globalMarginCheck,
      globalMaxQuantity,
      globalQuantity,
      globalSpread,
      globalSpreadOrderType,
      submitting,
    ]
  );

  const body = useMemo(() => {
    if (!hasGroupedData) {
      return <div className={modalStyles["modal-empty"]}>No data available</div>;
    }

    return groupedDataKeys.map((symbol) => {
      const groupData = groupedData[symbol];
      const rows = groupData.rows || [];
      const rollOverSymbol = getRollOverTradingSymbol(symbol);
      const groupExchange = rows[0]?.exchange || "NSE";

      return (
        <div key={symbol} className={modalStyles["modal-group-container"]}>
          <div className={modalStyles["modal-group"]}>
            <div className={modalStyles["modal-symbol-cell"]}>
              <span className={modalStyles["modal-symbol-label"]}>{symbol}</span>
            </div>
            <div className={modalStyles["modal-reprice-symbol-cell"]}>
              <InstrumentTradingSymbolField
                key={symbol}
                nearSymbol={symbol}
                value={rollOverSymbol}
                exchange={groupExchange}
                disabled={submitting}
                onChange={(newSymbol) => handleTradingSymbolChange(symbol, newSymbol)}
              />
            </div>
            <div className={modalStyles["modal-rows-container"]}>
              {rows.map((row) => {
                const quantity = getRowValue(row, "quantity");
                const maxQuantity = getRowValue(row, "maxQuantity");
                const spread = getRowValue(row, "spread");
                const spreadOrderType = getRowValue(row, "spreadOrderType");
                const marginCheck = getRowValue(row, "marginCheck");
                const quantitySpinners = createSpinnerHandlers(row, "quantity");
                const maxQuantitySpinners = createSpinnerHandlers(row, "maxQuantity");

                return (
                  <div key={row.modalId} className={modalStyles["modal-row"]}>
                    <div className={modalStyles["modal-account-cell"]}>
                      <span className={modalStyles["modal-account-label"]}>
                        {row.broker_client_code || "-"}
                      </span>
                    </div>
                    <div className={modalStyles["modal-quantity-cell"]}>
                      <NumericSpinnerField
                        value={quantity}
                        onChange={(e) => handleRowDataChange(row.modalId, "quantity", e.target.value)}
                        onIncrement={quantitySpinners.onIncrement}
                        onDecrement={quantitySpinners.onDecrement}
                      />
                    </div>
                    <div className={modalStyles["modal-max-quantity-cell"]}>
                      <NumericSpinnerField
                        value={maxQuantity}
                        onChange={(e) => handleRowDataChange(row.modalId, "maxQuantity", e.target.value)}
                        onIncrement={maxQuantitySpinners.onIncrement}
                        onDecrement={maxQuantitySpinners.onDecrement}
                      />
                    </div>
                    <div className={modalStyles["modal-spread-cell"]}>
                      <ModalBidAskField
                        orderType={spreadOrderType}
                        onOrderTypeChange={(e) =>
                          handleRowDataChange(row.modalId, "spreadOrderType", e.target.value)
                        }
                        value={spread}
                        onChange={(e) => handleRowDataChange(row.modalId, "spread", e.target.value)}
                        disabled={submitting}
                      />
                    </div>
                    <div className={modalStyles["modal-margin-check-cell"]}>
                      <ToggleSwitch
                        checked={Boolean(marginCheck)}
                        onChange={(checked) => handleRowDataChange(row.modalId, "marginCheck", checked)}
                        aria-label={`Margin check for ${row.broker_client_code || "account"}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    });
  }, [
    createSpinnerHandlers,
    getRollOverTradingSymbol,
    getRowValue,
    groupedData,
    groupedDataKeys,
    handleRowDataChange,
    handleTradingSymbolChange,
    hasGroupedData,
    submitting,
  ]);

  const footer = (
    <>
      <Button
        variant="outlined"
        onClick={handleClose}
        disabled={submitting}
        className={modalStyles["modal-cancel-button"]}
      >
        Cancel
      </Button>
      <Button
        variant="contained"
        onClick={handleConfirm}
        disabled={submitting || !hasGroupedData}
        className={modalStyles["modal-sqoff-button"]}
      >
        Roll Over{submitting ? "…" : ""}
      </Button>
    </>
  );

  return (
    <BookOrderDialogShell
      open={open}
      onClose={handleClose}
      paperClassName={modalStyles["roll-over-modal-dialog-paper"]}
      layoutVariant="rollOver"
      orderEntryGrid
      maxWidth={false}
      fullWidth={false}
      showTitleBar={false}
      header={header}
      footer={footer}
      closeDisabled={submitting}
    >
      {body}
    </BookOrderDialogShell>
  );
}

export default RollOverModal;
